/**
 * @copyright Copyright (c) Open-Xchange GmbH, Germany <info@open-xchange.com>
 * @license AGPL-3.0
 *
 * This code is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with OX App Suite. If not, see <https://www.gnu.org/licenses/agpl-3.0.txt>.
 *
 * Any use of the work other than as authorized under this license or copyright law is prohibited.
 */

import { defineStore } from 'pinia'
import { reactive, toRef } from 'vue'
import folderApi from '@/io.ox/core/folder/api'
import ox from '@/ox'

const defaultData = {
  id: '',
  folder_id: '',
  // value and protected are indeed strings
  used_for_sync: { value: 'false', protected: 'true' },
  title: ''
}

// simple store (initially just used for folder properties)
export const useFolderStore = defineStore('folder', () => {
  const folders = reactive({})
  const inflight = {}
  const updated = {}

  function get (folderId = '') {
    if (!folders[folderId]) folders[folderId] = { ...defaultData, id: folderId }
    return toRef(folders, folderId)
  }

  function update (folderId = '', data = {}) {
    const folder = get(folderId)
    Object.assign(folder.value, data)
    updated[folderId] = true
    return folder
  }

  async function fetch (folderId = '', { force = false } = {}) {
    if (!force && updated[folderId]) return get(folderId)
    if (!force && inflight[folderId]) return inflight[folderId]
    return (inflight[folderId] = folderApi.get(folderId, { cache: !force })
      .then(data => update(folderId, data))
      .always(() => delete inflight[folderId])
    )
  }

  async function setUsedForSync (folderId = '', enabled = false) {
    const folder = get(folderId)
    folder.value.used_for_sync.value = enabled ? 'true' : 'false'
    //  update(folderId, { used_for_sync: folder.value.used_for_sync })
    try {
      await folderApi.update(folderId, { used_for_sync: folder.value.used_for_sync })
      await fetch(folderId, { force: true })
    } catch (err) {
      console.error(err)
    }
  }

  return {
    get,
    update,
    fetch,
    setUsedForSync
  }
})

if (ox.debug) window.folderStore = useFolderStore()
